import { DragEvent, ReactNode } from 'react'
import { ButtonSizeOverrideContext } from '../Button/Button'
import Modal from '../Modal/Modal'
import PageHeadings from '../PageHeadings/PageHeadings'

type CanonicalModalProps = {
	open: boolean
	onClose: () => void
	onOpen?: () => void
	size?: 'small' | 'large'
	title: string
	subtitle?: ReactNode
	children: ReactNode
	actions: ReactNode
	isDragActive?: boolean
	onDragOver?: (e: DragEvent<HTMLDivElement>) => void
	onDragLeave?: (e: DragEvent<HTMLDivElement>) => void
	onDrop?: (e: DragEvent<HTMLDivElement>) => void
}

/**
 * Canonical modal template component for consistent UI/UX across modals.
 * Provides structured slots for heading, subtitle, content, and actions.
 *
 * Usage:
 * ```jsx
 * <CanonicalModal
 *   open={isOpen}
 *   onClose={onClose}
 *   title="Edit training user"
 *   subtitle="Test training (starts: 30/09/26)"
 * >
 *   {form fields and content here}
 * </CanonicalModal>
 * actions={<Button ... />}
 * ```
 */
function CanonicalModal({
	open,
	onClose,
	onOpen,
	size = 'small',
	title,
	subtitle,
	children,
	actions,
	isDragActive,
	onDragOver,
	onDragLeave,
	onDrop,
}: CanonicalModalProps) {
	return (
		<Modal open={open} onClose={onClose} onOpen={onOpen} size={size}>
			<div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} className='relative'>
				{/* Decorative drag-over highlight: absolutely positioned and pointer-events-none so it never
				    affects the content's box model/layout, and -inset-8 extends it to the Modal's own rounded-lg edge. */}
				<div
					aria-hidden='true'
					className={`pointer-events-none absolute -inset-8 rounded-lg border-2 transition-opacity motion-reduce:transition-none duration-150 ${
						isDragActive ? 'opacity-100 border-algae-300 bg-charcoal-900/50' : 'opacity-0 border-transparent'
					}`}
				/>
				{isDragActive && (
					<p className='sr-only' role='status' aria-live='polite'>
						Drop the CSV file to select it for upload
					</p>
				)}
				<div className='flex flex-col'>
					<PageHeadings title={title} />
					{subtitle && <div className='block text-charcoal-100'>{subtitle}</div>}
					{children}
					{/* Actions are always regular size (1rem), regardless of a `size` prop passed to the Button,
					    so modal action buttons stay consistent. Children above are unaffected: compact chips
					    and table-row buttons in the modal body keep their own size. */}
					<div className='mt-8 grow'>
						<ButtonSizeOverrideContext.Provider value='md'>{actions}</ButtonSizeOverrideContext.Provider>
					</div>
				</div>
			</div>
		</Modal>
	)
}

export default CanonicalModal
